import React from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Inbox, RefreshCw, RotateCcw } from "lucide-react";
import {
  fetchAdminPipeline,
  redriveAdminQueue,
  reprocessAdminActivity,
  type PipelineActivityCounts,
  type PipelineQueue,
} from "../../api/admin";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Skel } from "../../components/Skeleton";
import { StatBlock } from "../../components/StatBlock";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

// Pipeline health: SQS depths, activity processing counts, DLQ contents and
// the operator actions (re-run matching, redrive DLQ). Data comes from
// GET /admin/pipeline; incident checklists live in docs/ops-runbook.md.

const PIPELINE_KEY = ["admin/pipeline"];

const headerCell: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const mono: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  color: "var(--text-primary)",
};

function formatWhen(value: string | null | undefined) {
  if (!value) return "—";
  const d = new Date(value);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function errorMessage(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}

// ─── Re-run matching (single activity) ──────────────────────────────────────

export function RerunMatchingButton({
  activityId,
  label = "Re-run matching",
  size = "sm",
  onDone,
}: {
  activityId: string;
  label?: string;
  size?: "sm" | "md";
  onDone?: () => void;
}) {
  const mutation = useMutation({
    mutationFn: () => reprocessAdminActivity(activityId),
    onSuccess: () => onDone?.(),
  });

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <Button
        variant="secondary"
        size={size}
        leftIcon={<RotateCcw size={13} />}
        disabled={mutation.isPending}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          mutation.mutate();
        }}
      >
        {mutation.isPending ? "Queuing…" : mutation.isSuccess ? "Queued" : label}
      </Button>
      {mutation.isError && (
        <span style={{ fontSize: 12, color: "var(--status-danger)" }}>
          {errorMessage(mutation.error, "Failed")}
        </span>
      )}
    </span>
  );
}

// ─── Queue table ─────────────────────────────────────────────────────────────

function RedriveButton({ queue }: { queue: PipelineQueue }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => redriveAdminQueue(queue.key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PIPELINE_KEY });
    },
  });
  const pending = queue.dlq?.visible ?? 0;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
      <Button
        variant="secondary"
        size="sm"
        disabled={mutation.isPending || pending === 0 || !queue.dlq}
        onClick={() => {
          if (
            window.confirm(
              `Redrive ${pending.toLocaleString()} message${pending === 1 ? "" : "s"} from the ${queue.label} DLQ back onto its queue? They will be processed again straight away.`,
            )
          ) {
            mutation.mutate();
          }
        }}
      >
        {mutation.isPending ? "Starting…" : "Redrive DLQ"}
      </Button>
      {mutation.isError && (
        <span style={{ fontSize: 12, color: "var(--status-danger)" }}>
          {errorMessage(mutation.error, "Redrive failed")}
        </span>
      )}
    </span>
  );
}

function QueueTable({ queues }: { queues: PipelineQueue[] }) {
  const gridColumns = "minmax(140px,1.4fr) 90px 90px 90px 90px minmax(140px,1fr)";
  return (
    <div style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridColumns,
          gap: 12,
          padding: "4px 0 8px",
          borderBottom: "1px solid var(--border-subtle)",
          minWidth: 720,
        }}
      >
        <span style={headerCell}>Queue</span>
        <span style={{ ...headerCell, textAlign: "right" }}>Visible</span>
        <span style={{ ...headerCell, textAlign: "right" }}>In flight</span>
        <span style={{ ...headerCell, textAlign: "right" }}>DLQ</span>
        <span style={{ ...headerCell, textAlign: "right" }}>DLQ in flight</span>
        <span style={{ ...headerCell, textAlign: "right" }}>Actions</span>
      </div>
      {queues.map((q) => {
        const dlqCount = q.dlq?.visible ?? 0;
        return (
          <div
            key={q.key}
            style={{
              display: "grid",
              gridTemplateColumns: gridColumns,
              gap: 12,
              alignItems: "center",
              padding: "9px 0",
              borderBottom: "1px solid var(--border-subtle)",
              minWidth: 720,
            }}
          >
            <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: 13, color: "var(--text-primary)", display: "inline-flex", alignItems: "center", gap: 8 }}>
                {q.label}
                {dlqCount > 0 && (
                  <Badge variant="danger" icon={<AlertTriangle size={11} />}>
                    {dlqCount.toLocaleString()} dead
                  </Badge>
                )}
                {!q.configured && <Badge variant="neutral">Not configured</Badge>}
              </span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {q.error ?? q.queue?.name ?? "—"}
              </span>
            </span>
            <span className="tnum" style={{ ...mono, textAlign: "right" }}>{q.queue ? q.queue.visible.toLocaleString() : "—"}</span>
            <span className="tnum" style={{ ...mono, textAlign: "right" }}>{q.queue ? q.queue.notVisible.toLocaleString() : "—"}</span>
            <span className="tnum" style={{ ...mono, textAlign: "right", color: dlqCount > 0 ? "var(--status-danger)" : "var(--text-primary)" }}>
              {q.dlq ? q.dlq.visible.toLocaleString() : "—"}
            </span>
            <span className="tnum" style={{ ...mono, textAlign: "right" }}>{q.dlq ? q.dlq.notVisible.toLocaleString() : "—"}</span>
            <span style={{ display: "flex", justifyContent: "flex-end" }}>
              <RedriveButton queue={q} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Activity counts ─────────────────────────────────────────────────────────

function CountsRow({ label, counts, stuckAfterMinutes }: { label: string; counts: PipelineActivityCounts; stuckAfterMinutes: number }) {
  return (
    <div>
      <p className="eyebrow" style={{ fontSize: 10, marginBottom: 8 }}>{label}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 16 }}>
        <StatBlock size="sm" label="Imported" value={counts.total.toLocaleString()} />
        <StatBlock size="sm" label="Processed" value={counts.processed.toLocaleString()} />
        <StatBlock size="sm" label="Queued" value={counts.queued.toLocaleString()} sub={`under ${stuckAfterMinutes} min old`} />
        <StatBlock
          size="sm"
          label="Stuck"
          value={
            <span style={{ color: counts.stuck > 0 ? "var(--status-danger)" : undefined }}>
              {counts.stuck.toLocaleString()}
            </span>
          }
          sub={`unprocessed over ${stuckAfterMinutes} min`}
        />
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AdminPipelinePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const pipelineQuery = useQuery({
    queryKey: PIPELINE_KEY,
    queryFn: fetchAdminPipeline,
    enabled: !!user?.isAdmin,
    refetchInterval: 30_000,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const data = pipelineQuery.data;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: PIPELINE_KEY });
  const deadTotal = data?.queues.reduce((sum, q) => sum + (q.dlq?.visible ?? 0), 0) ?? 0;
  const healthy = data ? deadTotal === 0 && data.activities.total.stuck === 0 : false;

  const listGrid = "minmax(160px,2fr) minmax(140px,1.4fr) 100px 130px minmax(150px,1fr)";

  return (
    <AdminShell
      title="Pipeline"
      action={
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RefreshCw size={14} />}
          disabled={pipelineQuery.isFetching}
          onClick={refresh}
        >
          {pipelineQuery.isFetching ? "Refreshing…" : "Refresh"}
        </Button>
      }
    >
      {pipelineQuery.isError && (
        <div style={{ padding: "10px 14px", background: "var(--surface-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", fontSize: 13, color: "var(--status-danger)" }}>
          {errorMessage(pipelineQuery.error, "Could not load pipeline health")}
        </div>
      )}

      {data && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            background: "var(--surface-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            flexWrap: "wrap",
          }}
        >
          {healthy ? (
            <>
              <Badge variant="success" icon={<CheckCircle2 size={11} />}>Healthy</Badge>
              <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                No dead letters and nothing stuck. Auto-refreshes every 30 seconds.
              </span>
            </>
          ) : (
            <>
              <Badge variant="danger" icon={<AlertTriangle size={11} />}>Attention</Badge>
              <span style={{ fontSize: 13, color: "var(--text-primary)" }}>
                {deadTotal.toLocaleString()} dead letter{deadTotal === 1 ? "" : "s"} · {data.activities.total.stuck.toLocaleString()} stuck activit{data.activities.total.stuck === 1 ? "y" : "ies"}
              </span>
            </>
          )}
          <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-muted)" }}>
            as of {formatWhen(data.generatedAt)}
          </span>
        </div>
      )}

      <AtlasPanel title="Queues">
        {pipelineQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Skel height={30} />
            <Skel height={30} />
            <Skel height={30} />
          </div>
        ) : data ? (
          <QueueTable queues={data.queues} />
        ) : null}
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "12px 0 0" }}>
          Redrive moves every message in a DLQ back onto its source queue (SQS message move task). Each redrive is audit-logged.
        </p>
      </AtlasPanel>

      <AtlasPanel title="Activity processing">
        {pipelineQuery.isLoading ? (
          <Skel height={80} />
        ) : data ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <CountsRow label="Last 24 hours" counts={data.activities.last24h} stuckAfterMinutes={data.stuckAfterMinutes} />
            <CountsRow label="All time" counts={data.activities.total} stuckAfterMinutes={data.stuckAfterMinutes} />
          </div>
        ) : null}
      </AtlasPanel>

      <AtlasPanel title="Dead-lettered activities">
        {pipelineQuery.isLoading ? (
          <Skel height={60} />
        ) : !data || data.dlqFailures.length === 0 ? (
          <EmptyState
            icon={<Inbox size={20} />}
            title="Nothing in the process-activity DLQ"
            hint="Activities land here after three failed matching attempts."
            style={{ margin: "4px 0" }}
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: listGrid, gap: 12, padding: "4px 0 8px", borderBottom: "1px solid var(--border-subtle)", minWidth: 760 }}>
              <span style={headerCell}>Activity</span>
              <span style={headerCell}>User</span>
              <span style={headerCell}>Source</span>
              <span style={headerCell}>Enqueued</span>
              <span style={{ ...headerCell, textAlign: "right" }}>Actions</span>
            </div>
            {data.dlqFailures.map((f) => (
              <div key={f.messageId} style={{ display: "grid", gridTemplateColumns: listGrid, gap: 12, alignItems: "center", padding: "9px 0", borderBottom: "1px solid var(--border-subtle)", minWidth: 760 }}>
                <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.activityName ?? (f.activityDeleted ? "Activity deleted" : "Unparseable message")}
                  </span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
                    {f.activityId ?? f.messageId} · {f.receiveCount} attempt{f.receiveCount === 1 ? "" : "s"}
                  </span>
                </span>
                <span style={{ fontSize: 12, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.userId ? (
                    <Link to="/admin/users/$userId/activities" params={{ userId: f.userId }} style={{ color: "inherit" }}>
                      {f.userEmail ?? f.userId}
                    </Link>
                  ) : "—"}
                </span>
                <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{f.sourceType ?? "—"}</span>
                <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>{formatWhen(f.sentAt)}</span>
                <span style={{ display: "flex", justifyContent: "flex-end" }}>
                  {f.activityId && !f.activityDeleted ? <RerunMatchingButton activityId={f.activityId} onDone={refresh} /> : null}
                </span>
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "12px 0 0" }}>
          The failure reason is not stored on the activity. Search the process-activity worker log group in CloudWatch for the activity id (runbook: DLQ redrive).
        </p>
      </AtlasPanel>

      <AtlasPanel title="Stuck activities">
        {pipelineQuery.isLoading ? (
          <Skel height={60} />
        ) : !data || data.stuck.length === 0 ? (
          <EmptyState
            icon={<CheckCircle2 size={20} />}
            title="Nothing stuck"
            hint={`Every activity older than ${data?.stuckAfterMinutes ?? 15} minutes has been processed.`}
            style={{ margin: "4px 0" }}
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: listGrid, gap: 12, padding: "4px 0 8px", borderBottom: "1px solid var(--border-subtle)", minWidth: 760 }}>
              <span style={headerCell}>Activity</span>
              <span style={headerCell}>User</span>
              <span style={headerCell}>Source</span>
              <span style={headerCell}>Imported</span>
              <span style={{ ...headerCell, textAlign: "right" }}>Actions</span>
            </div>
            {data.stuck.map((a) => (
              <div key={a.id} style={{ display: "grid", gridTemplateColumns: listGrid, gap: 12, alignItems: "center", padding: "9px 0", borderBottom: "1px solid var(--border-subtle)", minWidth: 760 }}>
                <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <Link
                    to="/admin/users/$userId/activities/$activityId"
                    params={{ userId: a.userId, activityId: a.id }}
                    style={{ fontSize: 13, color: "var(--text-primary)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {a.name}
                  </Link>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
                    {a.hasGeom ? "has track" : "no track (needs re-upload)"}
                  </span>
                </span>
                <span style={{ fontSize: 12, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <Link to="/admin/users/$userId/activities" params={{ userId: a.userId }} style={{ color: "inherit" }}>
                    {a.userEmail ?? a.userId}
                  </Link>
                </span>
                <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{a.sourceType}</span>
                <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>{formatWhen(a.createdAt)}</span>
                <span style={{ display: "flex", justifyContent: "flex-end" }}>
                  {a.hasGeom ? <RerunMatchingButton activityId={a.id} onDone={refresh} /> : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </AtlasPanel>
    </AdminShell>
  );
}
